<template>
  <div>
    <div v-if="isLoading" class="text-center py-5 text-secondary">
      {{ $t("loadingData") }}
    </div>

    <template v-if="customerInformation && !isLoading">
      <section>
        <div class="d-flex flex-row align-items-center justify-content-between mb-4">
          <h5 class="m-0">{{ $t("customerInfo") }}</h5>
          <div class="d-flex flex-row align-items-center">
            <b-button class="mr-3" variant="outline-primary" v-if="isManager() || isUserAdmin()"
              :to="'/customers/edit/' + id">
              {{ $t("update") }}
            </b-button>
            <b-button class="mr-3" variant="outline-success" v-if="isManager() || isUserAdmin()"
              @click="onClickManageStorage(id)">
              {{ $t("capacity") }}
            </b-button>
            <b-button variant="outline-secondary" v-if="isManager() || isUserAdmin()" class="px-1" style="height:38px">
              <b-dropdown variant="outline" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
                <template v-slot:button-content>
                  <b-icon-three-dots-vertical />
                </template>
                <b-dropdown-item-button variant="danger" @click="onClickDeleteCustomer">{{ $t("deleteCustomer")
                  }}</b-dropdown-item-button>
                <b-dropdown-item-button style="display: none;" variant="danger" @click="onClickResTrictCustomer">{{
                  $t("restrictCustomer")}}</b-dropdown-item-button>
                <b-dropdown-divider class="custom-divider-zero" v-if="isUserAdmin() && customerInformation.customerStatus && (customerInformation.customerStatus.toLowerCase() === CustomerStatusEnum.ACTIVE || customerInformation.customerStatus.toLowerCase() === CustomerStatusEnum.INACTIVE)" />
                <b-dropdown-item-button v-if="isUserAdmin() && customerInformation.customerStatus && customerInformation.customerStatus.toLowerCase() === CustomerStatusEnum.ACTIVE"  variant="danger" @click="onClickWithDrawCustomer">{{ $t("withDrawCustomer")
                  }}</b-dropdown-item-button>
                <b-dropdown-item-button v-if="isUserAdmin() && customerInformation.customerStatus && customerInformation.customerStatus.toLowerCase() === CustomerStatusEnum.INACTIVE" variant="primary" @click="onClickUnlockAccountCustomer">
                  {{ $t("unlockAccountCustomer") }}
                </b-dropdown-item-button>
              </b-dropdown>
            </b-button>
          </div>
        </div>
        <b-row class="mb-4 pb-4 border-bottom">
          <b-col lg="6" class="border-right">
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("customerId") }}:</span></b-col>
              <b-col cols="8">{{ customerInformation.customerCode }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("customerName") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customerInformation.name }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("customerType") }}:</span></b-col>
              <b-col cols="8">{{ customerTypeText }}</b-col>
            </b-row>
            <b-row v-if="customerInformation.corporations">
              <b-col cols="4"><span class="text-secondary">{{ $t("underTheUnit") }}:</span></b-col>
              <b-col cols="8">{{ corporationsText }}</b-col>
            </b-row>
            <b-row v-else>
              <b-col cols="4"><span class="text-secondary">{{ $t("ownedBy") }}:</span></b-col>
              <b-col cols="8">{{ idTypeText }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">Email:</span></b-col>
              <b-col cols="8">{{ customerInformation.email }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("phoneNumber") }}:</span></b-col>
              <b-col cols="8">{{ customerInformation.phoneNumber }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("address") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customerInformation.address }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("status") }}:</span></b-col>
              <b-col cols="8" 
                     class="text-break" 
                     :class="{ 'text-danger': customerInformation.customerStatus && customerInformation.customerStatus.toLowerCase() === CustomerStatusEnum.INACTIVE }">
                {{ customerStatusMap[customerInformation.customerStatus] }}
              </b-col>
            </b-row>
          </b-col>
          <b-col lg="6">
            <b-row>
              <b-col cols="4">
                <span class="text-secondary">{{ $t("subscriptionPlan") }}:</span>
              </b-col>

              <b-col cols="8" v-if="activeSubscription">
                <!-- {{ activeSubscription.subscriptionPlanName }} - {{ $t(activeSubscription.remainingDays > 0 ? 'unexpired' :
                'expired') }} {{ activeSubscription.remainingDays }} ngày -->
                <strong>{{ activeSubscription.subscriptionPlanName }} | </strong>
                <RemainingDaysBadge :endTime="activeSubscription.endTime" :showBadge="false"/>
              </b-col>
              <b-col cols="8" v-else>
                {{ $t("cancelled") }}
              </b-col>

            </b-row>

            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("saleArea") }}:</span></b-col>
              <b-col cols="8">{{ customerInformation.areaName }} (id: {{ customerInformation.areaId }})</b-col>
            </b-row>
            <b-row v-if="customerInformation.taxCode">
              <b-col cols="4"><span class="text-secondary">{{ $t("taxCode") }}:</span></b-col>
              <b-col cols="8">{{ customerInformation.taxCode }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("documentType") }}:</span></b-col>
              <b-col cols="8">{{ customerInformation.documentTypeName }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("documentNumber") }}:</span></b-col>
              <b-col cols="8">{{ customerInformation.idCard }}</b-col>
            </b-row>

            <!-- Ẩn ngày cấp, ngày hết hạn, nơi cấp giấy phép 
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("issueDate") }}:</span></b-col>
              <b-col cols="8">{{ localDateFormat(customerInformation.licenseDate) }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("expDate") }}:</span></b-col>
              <b-col cols="8">{{ localDateFormat(customerInformation.licenseExpirationDate) }}</b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("issuePlace") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customerInformation.licensePlace }}</b-col>
            </b-row> -->
            <b-row v-if="activeSubscription && activeSubscription.storage">
              <b-col cols="4"><span class="text-secondary">{{ $t("storageCapacity") }}:</span></b-col>
              <b-col cols="8">{{ storageMaxValue }}GB/{{ activeSubscription.storage.retention ?
                activeSubscription.storage.retention + ' ngày' : '90 ngày' }} </b-col>
            </b-row>
            <b-row>
              <b-col cols="4"><span class="text-secondary">{{ $t("note") }}:</span></b-col>
              <b-col cols="8" class="text-break">{{ customerInformation.description }}</b-col>
            </b-row>
          </b-col>
        </b-row>
      </section>

      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h5 class="m-0">{{ $t("adminAccountList") }} ({{ adminUsers.length }})</h5>
        <b-button v-if="isManager()" variant="outline-primary" v-b-modal.new-customer-admin-modal>
          <b-icon icon="plus"></b-icon> {{ $t("createAdminAccount") }}
        </b-button>
      </div>

      <b-table :items="adminUsers" hover primary-key="id" :fields="tableFields" show-empty>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-if="isManager() || isUserAdmin()" v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right
            menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots" />
            </template>
            <b-dropdown-item-button @click="onClickEditAdminUser(data.item)">{{ $t("updateInfo")
              }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickResetAdminUserPassword(data.item)">{{ $t("resetPassword")
              }}</b-dropdown-item-button>
            <b-dropdown-item-button @click="onClickUnlockAccount(data.item)">{{ $t("unlockAccount")
              }}</b-dropdown-item-button>
            <b-dropdown-item-button variant="danger" @click="onClickDeleteAdminUser(data.item)">{{ $t("actionDelete")
              }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </template>

    <b-modal id="new-customer-admin-modal" :title="$t('createCustomerAdminAccount')" dialog-class="min-width-700"
      hide-footer no-close-on-backdrop>
      <NewCustomerAdminUserModal :customer-id="id" @submitted="fetchCustomer" />
    </b-modal>

    <b-modal id="edit-customer-user-modal" :title="$t('updateAccountInfo')" hide-footer no-close-on-backdrop>
      <EditCustomerUserModal :editing-user="editingUser" :show-voice-text-notice="showVoiceTextNotice"
        @submitted="fetchCustomer" />
    </b-modal>

    <b-modal id="reset-password-modal" :title="$t('resetPassword')" hide-footer no-close-on-backdrop>
      <ResetPasswordModal :target-user="editingUser" @submitted="fetchCustomer" />
    </b-modal>
    <b-modal id="manage-storage-modal"  size ="lg" :title="$t('manageStorage')" ok-only
      ok-variant="primary" :ok-title="$t('close')">
      <ManageStorageModal modal-id="manage-storage-modal" :customerId="customerId" />
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.customer-info-text {
  font-size: 17px;
  line-height: 175%;

  &>*> :first-child {
    flex-shrink: 0;
    margin-right: 1rem;
  }

  &>*> :nth-child(2) {
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>

<script lang="ts">
import { Component } from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";
import NewCustomerAdminUserModal from "@/components/customer/NewCustomerAdminModal.vue";
import { BvTableFieldArray } from "bootstrap-vue";
import * as userService from "@/service/userService";
import * as customerService from "@/service/customerService";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import EditCustomerUserModal from "@/components/customer/user-accounts/EditCustomerUserModal.vue";
import ResetPasswordModal from "@/components/account/ResetPasswordModal.vue";
import { CustomerActionEnum } from "@/views/enums/customerAction";
import { CustomerStatusEnum } from "@/views/enums/customerStatus";
import ManageStorageModal from "@/components/storage/ManageStorageModal.vue";
import * as util from "@/service/util";
import RemainingDaysBadge from "@/views/customer/details/RemainingDaysBadge.vue";

@Component({
  components: { EditCustomerUserModal, NewCustomerAdminUserModal, ResetPasswordModal, ManageStorageModal, RemainingDaysBadge }
})
export default class CustomerDetailsOverview extends BaseComponent {
  CustomerStatusEnum = CustomerStatusEnum;

  readonly customerStatusMap = {
    [CustomerStatusEnum.ACTIVE]: this.$t("customerStatusActive").toString(),
    [CustomerStatusEnum.INACTIVE]: this.$t("inActive").toString(),
    [CustomerStatusEnum.CANCELLED]: this.$t("customerStatusCancelled").toString()
  };

  readonly customerStatusBadgeVariantMap = {
    [CustomerStatusEnum.ACTIVE]: "primary",
    [CustomerStatusEnum.INACTIVE]: "secondary",
    [CustomerStatusEnum.CANCELLED]: "danger"
  };

  id: number = null;

  isLoading = false;
  customerInformation = null;
  customerSubscriptions = [];

  showVoiceTextNotice = false;
  customerId: number = null;

  /// Giá trị dung lượng tối đa + dung lượng đã mua thêm
  storageMaxValue: number = 0;

  adminUsers = [];
  tableFields: BvTableFieldArray = [
    {
      key: "id",
      label: "#"
    },
    {
      key: "name",
      label: this.$t("fullName").toString(),
    },
    {
      key: "username",
      label: "Username",
    },
    {
      key: "email",
      label: "Email"
    },
    {
      key: "role",
      label: this.$t("role").toString(),
      formatter: value => ({ Customer: this.$t("roleAdmin").toString(), User: this.$t("roleUser").toString() }[value] || value)
    },
    {
      key: "createdAt",
      label: this.$t("createdDate").toString()
    },
    {
      key: "actions",
      label: ""
    }
  ];

  editingUser = null;


  mounted() {
    this.id = +this.$route.params.id || null;
    this.fetchCustomer();
  }

  async fetchCustomer() {
    try {
      this.isLoading = true;
      //const fetchCustomerPromise = customerService.getDetails(this.id);
      // const fetchDocumentTypesPromise = customerService.getDocumentTypes();  
      // const documentTypes = await fetchDocumentTypesPromise;
      // this.customerInformation.documentTypeName = documentTypes.find(it => it.id === this.customerInformation.documentTypeId)?.title;
      //const fetchSubscriptionsPromise = subscriptionPlanService.getCustomerSubscription(this.id);
      //const fetchAdminUsersPromise = userService.getCustomerAdmins(this.id);
      this.customerInformation = await customerService.getDetails(this.id);
      this.customerSubscriptions = await subscriptionPlanService.getCustomerSubscription(this.id);
      this.adminUsers = await userService.getCustomerAdmins(this.id);
      this.updateProps();
    } catch (e) {
      this.toastError(e);

    } finally {
      this.isLoading = false;
    }
  }


  get activeSubscription() {
    return this.customerSubscriptions?.find(it => it.isActive);
  }
  get customerTypeText(): string {
    const type = this.customerInformation?.customerType?.toLowerCase();
    return this.$t(type).toString();
  }
  get corporationsText(): string {
    const type = this.customerInformation?.corporations?.toLowerCase();
    return this.$t(type).toString();
  }

  get idTypeText(): string {
    const type = this.customerInformation?.idType?.toLowerCase();
    return this.$t(type).toString();
  }

  /// Cập nhật các props của page
  updateProps() {
    this.storageMaxValue = util.bytesToGB(util.getMaxStorage(this.customerInformation?.maxStorageSize ?? 0, this.customerInformation?.purchasedStorageSize ?? 0), true, 2);
  }

  onClickEditAdminUser(user) {
    this.editingUser = user;
    // Kiểm tra nếu là UserAdmin và customer.activeVoiceText bật
    if (this.customerInformation.activeVoiceText == 1) {
      this.showVoiceTextNotice = true;
    } else {
      this.showVoiceTextNotice = false;
    }
    this.$bvModal.show("edit-customer-user-modal");
  }

  onClickResetAdminUserPassword(user) {
    this.editingUser = user;
    this.$bvModal.show("reset-password-modal");
  }
  
  // Mở khóa tài khoản con
  async onClickUnlockAccount(user) {
    const modalMessage = this.$t("unlockAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'primary',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await userService.unlockAccount(user.id);
        this.fetchCustomer();
        this.toastSuccessDelay(this.$t("unlockAccountSuccess").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }

  // Mở khóa khách hàng
  async onClickUnlockAccountCustomer() {
    const modalMessage = this.$t("unlockAccountConfirmation", { account: this.customerInformation.name }).toString() + `(ID: ${this.customerInformation.id})?`;
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'primary',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await userService.unlockAccountCustomer(this.customerInformation.id,"Unclock Account");
        this.fetchCustomer();
        this.toastSuccessDelay(this.$t("unlockAccountSuccess").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }

  async onClickDeleteAdminUser(user) {
    const modalMessage = this.$t("deleteAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await userService.deleteUser(user.id);
        this.fetchCustomer();
        this.toastSuccessDelay(this.$t("deleteAccountSuccess").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }
  
  //xóa khách hàng
  async onClickDeleteCustomer() {
    const modalMessage = this.$t("deleteCustomerConfirmation", { name: this.customerInformation.name }).toString();
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await customerService.deleteCustomer(this.id, CustomerActionEnum.DELETE);
        this.$router.replace("/customers");
        this.toastSuccessDelay(this.$t("deleteCustomerSuccess").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }

  //hạn chế/phản đối
  async onClickResTrictCustomer() {
    const modalMessage = this.$t("restrictCustomerConfirmation", { name: this.customerInformation.name }).toString();
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await customerService.deleteCustomer(this.id, CustomerActionEnum.REJECT);;
        this.$router.replace("/customers");
        this.toastSuccessDelay(this.$t("deleteCustomerSuccess").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }

  //rút lại đồng ý
  async onClickWithDrawCustomer() {
    const modalMessage = this.$t("withDrawCustomerConfirmation", { name: this.customerInformation.name }).toString();
    const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
      okVariant: 'danger',
      okTitle: this.$t("yes").toString(),
      cancelTitle: this.$t("no").toString(),
    });

    if (confirmDelete) {
      try {
        this.showLoading();
        await customerService.withdrawCustomerConsent(this.id, "WithdrawCustomerConsent");
        this.fetchCustomer();
        this.toastSuccessDelay(this.$t("withDrawCustomerSuccess").toString());

      } catch (e) {
        this.toastError(e);

      } finally {
        this.hideLoading();
      }
    }
  }


  onClickManageStorage(id) {
    this.customerId = id;
    this.$bvModal.show("manage-storage-modal");
  }
}
</script>
